import React, { useState } from "react";
import { Button, Collapse, Modal, Space, Typography } from "antd";
import { CheckCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

const { Text, Title } = Typography;
const { Panel } = Collapse;

const StockChangeReasonModal = ({ open, onCancel, onConfirm }) => {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = useState(null);

  const handleConfirm = () => {
    if (selectedReason !== null) {
      onConfirm(selectedReason === "incoming");
      setSelectedReason(null);
    }
  };

  const handleCancel = () => {
    setSelectedReason(null);
    onCancel();
  };

  return (
    <StyledModal
      open={open}
      onCancel={handleCancel}
      footer={null}
      width="100%"
      style={{ maxWidth: "500px", top: 20 }}
      centered
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Title level={4} style={{ margin: 0, textAlign: "center" }}>
          {t("unpack_order.why_changing_stock")}
        </Title>

        <ChoiceContainer>
          <ChoiceCard selected={selectedReason === "incoming"} onClick={() => setSelectedReason("incoming")}>
            {selectedReason === "incoming" && (
              <CheckIcon>
                <CheckCircleOutlined />
              </CheckIcon>
            )}
            <ChoiceText strong>{t("unpack_order.reason_incoming_products")}</ChoiceText>
          </ChoiceCard>

          <ChoiceCard selected={selectedReason === "other"} onClick={() => setSelectedReason("other")}>
            {selectedReason === "other" && (
              <CheckIcon>
                <CheckCircleOutlined />
              </CheckIcon>
            )}
            <ChoiceText strong>{t("unpack_order.reason_other")}</ChoiceText>
          </ChoiceCard>
        </ChoiceContainer>

        <Collapse ghost expandIconPosition="end" style={{ background: "transparent" }}>
          <Panel
            header={
              <HelpHeader>
                <InfoCircleOutlined style={{ marginRight: 8 }} />
                <Text type="secondary">{t("unpack_order.help_me_choose")}</Text>
              </HelpHeader>
            }
            key="1"
          >
            <HelpContent>
              <Space direction="vertical" size="middle">
                <div>
                  <Text strong>{t("unpack_order.reason_incoming_products")}:</Text>
                  <br />
                  <Text type="secondary">{t("unpack_order.incoming_explanation")}</Text>
                </div>
                <div>
                  <Text strong>{t("unpack_order.reason_other")}:</Text>
                  <br />
                  <Text type="secondary">{t("unpack_order.other_explanation")}</Text>
                </div>
                <div style={{ marginTop: 16, padding: "12px", background: "#fff7e6", borderRadius: "4px" }}>
                  <Text type="warning" strong>
                    {t("unpack_order.when_in_doubt")}
                  </Text>
                </div>
              </Space>
            </HelpContent>
          </Panel>
        </Collapse>

        <ButtonContainer>
          <Button onClick={handleCancel} block size="large">
            {t("cancel")}
          </Button>
          <Button type="primary" onClick={handleConfirm} disabled={selectedReason === null} block size="large">
            {t("confirm")}
          </Button>
        </ButtonContainer>
      </Space>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
    padding: 24px 16px;
  }

  .ant-modal-body {
    padding: 0;
  }

  @media (max-width: 576px) {
    max-width: calc(100vw - 32px) !important;
    margin: 16px;
  }
`;

const ChoiceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const ChoiceCard = styled.div`
  position: relative;
  padding: 20px;
  border: 2px solid ${(props) => (props.selected ? "#1890ff" : "#d9d9d9")};
  border-radius: 8px;
  background: ${(props) => (props.selected ? "#e6f7ff" : "#ffffff")};
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: #1890ff;
    box-shadow: 0 2px 8px rgba(24, 144, 255, 0.2);
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 576px) {
    padding: 16px;
    min-height: 56px;
  }
`;

const CheckIcon = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  color: #1890ff;
  font-size: 20px;
`;

const ChoiceText = styled(Text)`
  font-size: 16px;
  text-align: center;

  @media (max-width: 576px) {
    font-size: 14px;
  }
`;

const HelpHeader = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const HelpContent = styled.div`
  padding: 12px 0;
  font-size: 14px;

  @media (max-width: 576px) {
    font-size: 13px;
  }
`;

const ButtonContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
  margin-top: 8px;

  @media (max-width: 576px) {
    gap: 8px;
  }
`;

export default StockChangeReasonModal;
